import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CryptoWorkerService {
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingMessages = new Map<number, { resolve: (value: any) => void; reject: (reason?: any) => void }>();
  private workerAvailable = false;

  constructor() {
    this.initWorker();
  }

  private initWorker(): void {
    try {
      this.worker = new Worker('workers/crypto-worker.js');

      this.worker.onmessage = (e): void => {
        const { id, result, error } = e.data;
        const pending = this.pendingMessages.get(id);

        if (pending) {
          this.pendingMessages.delete(id);
          if (error) {
            pending.reject(new Error(error));
          } else {
            pending.resolve(result);
          }
        }
      };

      this.worker.onerror = (error): void => {
        console.warn('Crypto worker failed to load, falling back to main thread operations');
        this.workerAvailable = false;
        this.worker = null;

        // Reject any pending operations and they will fall back to main thread
        this.pendingMessages.forEach(({ reject }) => {
          reject(new Error('Worker not available'));
        });
        this.pendingMessages.clear();
      };

      // Test if worker is working by sending a simple message
      this.worker.postMessage({ id: 0, method: 'test', args: [] });
      this.workerAvailable = true;
    } catch (error) {
      console.warn('Failed to initialize crypto worker, using main thread fallback');
      this.workerAvailable = false;
      this.worker = null;
    }
  }

  private postMessage(method: string, args: any[]): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.workerAvailable) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = ++this.messageId;
      this.pendingMessages.set(id, { resolve, reject });

      try {
        this.worker.postMessage({ id, method, args });
      } catch (error) {
        this.pendingMessages.delete(id);
        reject(error);
      }
    });
  }

  // Most intensive crypto operations - these will run in worker
  public symmetricEncrypt(
    key: Uint8Array,
    plain: Uint8Array,
    iv: Uint8Array,
    header?: Uint8Array,
  ): Promise<Uint8Array> {
    return this.postMessage('symmetricEncrypt', [key, plain, iv, header]);
  }

  public symmetricDecrypt(
    key: Uint8Array,
    crypted: Uint8Array,
    iv: Uint8Array,
    header?: Uint8Array,
  ): Promise<Uint8Array> {
    return this.postMessage('symmetricDecrypt', [key, crypted, iv, header]);
  }

  public keyStretch(password: string, salt: Uint8Array, iterations: number, length: number): Promise<Uint8Array> {
    return this.postMessage('keyStretch', [password, salt, iterations, length]);
  }

  public getApiHmac(key: ArrayLike<number>, data: ArrayLike<number>): Promise<number[]> {
    return this.postMessage('getApiHmac', [key, data]);
  }

  public passwordDecrypt(b64Crypted: string, password: string, iterations: number): Promise<string> {
    return this.postMessage('passwordDecrypt', [b64Crypted, password, iterations]);
  }

  public destroy(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingMessages.clear();
  }
}
